import './ProductRoadmap.scss';
import { useEffect, useMemo, useState } from 'react';
import { EmptyState } from '@Components/EmptyState';
import { Page } from '@Components/Page';
import { Spinner } from '@Components/Spinner';
import { useSetError } from '@src/redux/modals';
import { useTabbedNav } from '@src/utils/hooks/useNav';
import { EffortImpactView } from './EffortImpactView';
import {
  FilterFacet,
  FilterFacetType,
  PAGE_TITLE,
  Product,
  ProductRoadmapData,
  ROADMAP_TABS,
  RoadmapTab,
  RoadmapTabType
} from './product-roadmap-constants';
import {
  defaultFacetSelection,
  emptyFacetSelection,
  facetColorMap,
  FacetSelection,
  filterProducts,
  scopedProductsByFacet
} from './product-roadmap-utils';
import { ProductCard } from './ProductCard';
import { ProductRoadmapStatusBar } from './ProductRoadmapFilters';
import { ProductRoadmapTable } from './ProductRoadmapTable';
import { useProductRoadmapData } from './useProductRoadmapData';

interface ProductRoadmapBodyProps {
  data: ProductRoadmapData | undefined;
  isLoading: boolean;
  tab: RoadmapTabType;
}

const ProductRoadmapBody = ({ data, isLoading, tab }: ProductRoadmapBodyProps) => {
  // Facet filters live here, above both tabs, so a filter set on the Roadmap tab also applies to
  // the Effort x Impact chart (and the chart says so, with a way to clear them). Until someone
  // touches a filter, the page shows its default -- every status except Deployed -- which needs
  // the data to compute, hence `null` = "still on the default".
  const [userSelection, setUserSelection] = useState<FacetSelection | null>(null);
  // The table owns its own card; the chart's card is opened from here.
  const [chartCardProduct, setChartCardProduct] = useState<Product | null>(null);

  const products = useMemo(() => data?.products ?? [], [data]);
  const defaultSelection = useMemo(() => defaultFacetSelection(products), [products]);
  const selection = userSelection ?? defaultSelection;
  // Deployed and parked products are never on the chart, so the default status filter doesn't
  // count as filtering it; only filters that drop an in-scope product do.
  const totalInScope = useMemo(() => products.filter(p => p.assessable).length, [products]);

  const functionColorMap = useMemo(
    () => facetColorMap(Array.from(new Set(products.flatMap(p => p.function)))),
    [products]
  );

  const scopedProducts = useMemo(
    () => scopedProductsByFacet(products, selection),
    [products, selection]
  );
  const filteredProducts = useMemo(
    () => filterProducts(products, selection),
    [products, selection]
  );

  const handleFacetChange = (facet: FilterFacetType, next: ReadonlySet<string>) => {
    setUserSelection(prev => ({ ...(prev ?? defaultSelection), [facet]: next }));
  };

  if (isLoading) return <Spinner />;
  if (products.length === 0) return <EmptyState title='No product data loaded.' />;

  return (
    <div className='product-roadmap-body'>
      <p className='product-roadmap-lede'>
        {tab === RoadmapTab.EFFORT_IMPACT
          ? 'Every product currently in build.'
          : 'Every product we operate and everything currently in build.'}
      </p>
      <p className='product-roadmap-meta'>
        {data?.meta.version} · as of {data?.meta.asof}
      </p>
      {tab === RoadmapTab.EFFORT_IMPACT ? (
        <>
          <EffortImpactView
            products={filteredProducts}
            totalInScope={totalInScope}
            meta={data?.meta.effortImpact}
            functionColorMap={functionColorMap}
            onOpenCard={setChartCardProduct}
            onResetFilters={() => setUserSelection(null)}
          />
          <ProductCard
            product={chartCardProduct}
            functionColorMap={functionColorMap}
            onHide={() => setChartCardProduct(null)}
          />
        </>
      ) : (
        <>
          <ProductRoadmapStatusBar
            products={filteredProducts}
            scopedProducts={scopedProducts[FilterFacet.STATUS]}
            selection={selection}
            totalCount={products.length}
            onChange={next => handleFacetChange(FilterFacet.STATUS, next)}
            onReset={() => setUserSelection(emptyFacetSelection())}
          />
          <ProductRoadmapTable
            products={filteredProducts}
            scopedProducts={scopedProducts}
            selection={selection}
            functionColorMap={functionColorMap}
            onFacetChange={handleFacetChange}
          />
        </>
      )}
    </div>
  );
};

export const ProductRoadmap = () => {
  const { data, isLoading, error } = useProductRoadmapData();
  const setError = useSetError();
  const { tab, goToTab } = useTabbedNav<RoadmapTabType>({
    baseURL: 'product-roadmap',
    defaultKey: RoadmapTab.ROADMAP,
    navs: ROADMAP_TABS.map(t => t.key)
  });

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load the product roadmap",
        message: `Failed to load product roadmap data: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  return (
    <Page
      title={PAGE_TITLE}
      pageType='product-roadmap'
      isLoading={isLoading}
      navs={ROADMAP_TABS}
      selectedNav={tab}
      onNav={nav => goToTab(nav as RoadmapTabType)}
    >
      <ProductRoadmapBody data={data} isLoading={isLoading} tab={tab} />
    </Page>
  );
};
